import { type CurrentUser, isPlatformAdmin, requireAdmin, signOut } from '../lib/auth';
import { apply, bindLangSwitchers, t } from '../i18n/i18n';
import { ADMIN_NAV, navLabel, renderSideNav } from './nav';
import { envConfig } from '../../envConfig';

export interface ShellOptions {
  /** Matches a {@link NavItem.id}; controls which side-nav row gets `is-active`. */
  section?: string;
  /** Breadcrumb shown after "Beheerportaal" in the top bar. Defaults to the active section's label. */
  crumb?: string;
}

/**
 * Shared admin-page bootstrap: gates the page on admin role, renders the
 * designer chrome (top bar + left side nav) into the slots, and wires the
 * sign-out button. Returns the current user once access is granted.
 *
 * Backward-compatible signature: passing a string is equivalent to
 * <c>{ section: theString }</c>, so the legacy callers keep working.
 *
 * Page HTML must contain <c>&lt;header id="admin-bar"&gt;&lt;/header&gt;</c>
 * and <c>&lt;aside id="admin-side"&gt;&lt;/aside&gt;</c> (or the corresponding
 * `class="admin-bar"` / `class="admin-side"` elements). Anything pre-existing
 * inside <c>#admin-side</c> stays - the helper appends the shared nav below
 * it, which lets pages like admin-requests keep their page-specific filter
 * group on top.
 */
export async function initAdminShell(sectionOrOpts?: string | ShellOptions): Promise<CurrentUser> {
  const opts: ShellOptions = typeof sectionOrOpts === 'string' ? { section: sectionOrOpts } : (sectionOrOpts ?? {});
  const section = opts.section ?? '';

  const user = await requireAdmin('/login.html');

  // Find the matching nav entry once - its translated label doubles as the
  // top-bar crumb when the caller hasn't supplied an explicit one.
  const navItem = ADMIN_NAV.find((i) => i.id === section);

  // Hard guard for adminOnly pages: a signed-in partner who hits one of these
  // URLs directly (typed into the bar, browser history) gets bounced back to
  // the dashboard rather than seeing a half-loaded admin page that 403s on
  // every API call.
  if (navItem?.adminOnly && !isPlatformAdmin(user.role)) {
    window.location.replace('/admin.html');
    throw new Error('not platform admin');
  }

  // Same bounce for pages gated behind a brand feature that's off on this
  // customer (e.g. Bristol typing /admin-scan-projects.html when the
  // Energie-scan module isn't part of the Bristol bundle).
  if (navItem?.featureFlag && !envConfig.brand.features[navItem.featureFlag]) {
    window.location.replace('/admin.html');
    throw new Error('feature disabled on this customer');
  }

  const crumb = opts.crumb ?? (navItem ? navLabel(navItem) : '');
  injectTopBar(user, crumb);
  injectSideNav(section, user.role);
  wireLogout();

  // Translate any static data-i18n markup the page itself shipped (h1, table
  // headers, etc.). Safe even when the page has no i18n attrs.
  apply();
  // Wire the lang switcher we just injected into the top bar (initI18n's
  // first run can't see it because the bar is JS-rendered).
  bindLangSwitchers();

  // Auth verified - reveal the page. The body wears [data-auth-pending] in
  // the HTML so the admin chrome doesn't flash before requireAdmin resolves;
  // failed auth redirects above (this code never runs) and the attribute
  // stays in place until the new page loads.
  document.body.removeAttribute('data-auth-pending');

  return user;
}

function injectTopBar(user: CurrentUser, crumb: string): void {
  const bar = document.getElementById('admin-bar') ?? document.querySelector<HTMLElement>('.admin-bar');
  if (!bar) return;
  bar.classList.add('admin-bar');
  const initials = (user.name || user.email).slice(0, 2).toUpperCase();
  const portal = t('admin.bar.portal');
  bar.innerHTML = `
    <div class="admin-bar__left">
      <a href="/index.html" aria-label="${escapeText(t('admin.bar.back_to_public'))}">
        <img class="admin-bar__logo" src="${escapeText(envConfig.brand.logoWhiteSrc)}" alt="${escapeText(envConfig.brand.title)}"/>
      </a>
      <span class="admin-bar__crumb">${crumb ? `<a href="/admin.html" style="color:inherit;text-decoration:none;opacity:0.6;">${escapeText(portal)}</a> · ${escapeText(crumb)}` : escapeText(portal)}</span>
    </div>
    <div class="admin-bar__right">
      <div class="lang-switcher" data-lang-switcher role="group" aria-label="Taal kiezen">
        <button type="button" data-lang="nl" aria-label="Nederlands">NL</button>
        <button type="button" data-lang="en" aria-label="English">EN</button>
      </div>
      <div class="admin-bar__user" title="${escapeText(user.role)}">
        <span class="admin-bar__avatar" id="user-avatar">${escapeText(initials)}</span>
        <span id="user-name">${escapeText(user.name || user.email)}</span>
      </div>
      <a class="admin-bar__logout" href="#" id="logout-btn">${escapeText(t('admin.bar.logout'))}</a>
    </div>
  `;
}

function injectSideNav(activeId: string, userRole: string): void {
  const side = document.getElementById('admin-side') ?? document.querySelector<HTMLElement>('.admin-side');
  if (!side) return;
  side.classList.add('admin-side');
  side.setAttribute('aria-label', t('admin.bar.menu_label'));
  // Preserve any page-specific groups (e.g. the request filter list on
  // admin-requests.html); the shared nav is appended *after* them.
  side.insertAdjacentHTML('beforeend', renderSideNav(activeId, userRole));
}

function wireLogout(): void {
  const btn = document.getElementById('logout-btn');
  if (!btn) return;
  btn.addEventListener('click', async (e) => {
    e.preventDefault();
    await signOut();
    window.location.replace('/login.html');
  });
}

function escapeText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/* ---- helpers retained for backward compatibility with existing pages ---- */

export function showError(boxId: string, message: string): void {
  const box = document.getElementById(boxId);
  if (!box) return;
  box.textContent = message;
  box.hidden = false;
}

export function hideError(boxId: string): void {
  const box = document.getElementById(boxId);
  if (box) box.hidden = true;
}

export function escapeHtml(value: string): string {
  return escapeText(value);
}

// Re-exported so existing `from './shell'` imports keep working; the
// implementation lives in lib/busy.ts so public pages can share it.
export { setButtonBusy } from '../lib/busy';

interface ConfirmModalOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Use the danger button variant for the confirm action. Defaults to true since this helper is used mostly for deletes. */
  danger?: boolean;
}

/**
 * Promise-based replacement for window.confirm - renders the designer's
 * .modal-overlay/.modal markup, returns true on confirm, false on cancel,
 * backdrop click, or Escape. Reuses (and lazily creates) a single overlay
 * element at the end of <body> so multiple callers don't fight over the DOM.
 */
export function confirmModal(opts: ConfirmModalOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const cancelLabel = opts.cancelLabel ?? t('admin.actions.cancel');
    const confirmLabel = opts.confirmLabel ?? t('admin.actions.save');
    const confirmClass = opts.danger === false ? 'btn btn--primary' : 'btn btn--danger';

    let overlay = document.getElementById('admin-confirm-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'admin-confirm-overlay';
      overlay.className = 'modal-overlay';
      document.body.appendChild(overlay);
    }
    overlay.innerHTML = `
      <div class="modal" role="dialog" aria-modal="true" aria-labelledby="admin-confirm-title">
        <div class="modal__head">
          <div id="admin-confirm-title" class="modal__title"></div>
          <button type="button" class="modal__close" aria-label="${escapeText(cancelLabel)}">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg>
          </button>
        </div>
        <div class="modal__body">
          <p id="admin-confirm-message" style="margin:0;color:var(--sk-navy-800);font-size:15px;line-height:1.5;"></p>
        </div>
        <div class="modal__foot">
          <button type="button" class="btn btn--ghost" data-role="cancel"></button>
          <button type="button" class="${confirmClass}" data-role="confirm"></button>
        </div>
      </div>
    `;
    const titleEl   = overlay.querySelector<HTMLElement>('#admin-confirm-title')!;
    const messageEl = overlay.querySelector<HTMLElement>('#admin-confirm-message')!;
    const cancelBtn = overlay.querySelector<HTMLButtonElement>('[data-role="cancel"]')!;
    const confirmBtn= overlay.querySelector<HTMLButtonElement>('[data-role="confirm"]')!;
    const closeBtn  = overlay.querySelector<HTMLButtonElement>('.modal__close')!;
    titleEl.textContent   = opts.title;
    // pre-line so callers can separate paragraphs with \n\n, matching what
    // the window.confirm messages this replaces used to do.
    messageEl.style.whiteSpace = 'pre-line';
    messageEl.textContent = opts.message;
    cancelBtn.textContent = cancelLabel;
    confirmBtn.textContent= confirmLabel;

    const close = (result: boolean): void => {
      overlay!.classList.remove('is-open');
      document.removeEventListener('keydown', onKey);
      resolve(result);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close(false);
      else if (e.key === 'Enter') close(true);
    };
    cancelBtn.addEventListener('click', () => close(false));
    closeBtn.addEventListener('click', () => close(false));
    confirmBtn.addEventListener('click', () => close(true));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(false); });
    document.addEventListener('keydown', onKey);

    overlay.classList.add('is-open');
    confirmBtn.focus();
  });
}

interface PromptModalOptions {
  title: string;
  /** Optional lede shown above the input. */
  message?: string;
  /** Label above the input field. */
  label: string;
  /** Initial input value (e.g. the current value when editing). */
  value?: string;
  placeholder?: string;
  maxLength?: number;
  /** inputmode attribute, e.g. 'numeric' for digit-only values. */
  inputMode?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Returns an error message to show under the input, or null when the value
   * is acceptable. Confirming with an invalid value keeps the modal open.
   */
  validate?: (value: string) => string | null;
}

/**
 * Promise-based replacement for window.prompt - same designer modal as
 * <see>confirmModal</see> but with a single text input and inline validation.
 * Resolves with the trimmed value on confirm, or null on cancel, backdrop
 * click, or Escape.
 */
export function promptModal(opts: PromptModalOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const cancelLabel = opts.cancelLabel ?? t('admin.actions.cancel');
    const confirmLabel = opts.confirmLabel ?? t('admin.actions.save');

    let overlay = document.getElementById('admin-prompt-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'admin-prompt-overlay';
      overlay.className = 'modal-overlay';
      document.body.appendChild(overlay);
    }
    overlay.innerHTML = `
      <div class="modal" role="dialog" aria-modal="true" aria-labelledby="admin-prompt-title">
        <div class="modal__head">
          <div id="admin-prompt-title" class="modal__title"></div>
          <button type="button" class="modal__close" aria-label="${escapeText(cancelLabel)}">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg>
          </button>
        </div>
        <div class="modal__body">
          <p id="admin-prompt-message" style="display:none;margin:0 0 14px;color:var(--sk-muted);font-size:13px;line-height:1.5;white-space:pre-line;"></p>
          <div class="form-field" style="margin-bottom:0;">
            <label for="admin-prompt-input" id="admin-prompt-label"></label>
            <input id="admin-prompt-input" type="text" autocomplete="off"/>
            <small id="admin-prompt-error" style="display:none;margin-top:6px;font-size:12px;color:var(--sk-status-occupied,#c0392b);"></small>
          </div>
        </div>
        <div class="modal__foot">
          <button type="button" class="btn btn--ghost" data-role="cancel"></button>
          <button type="button" class="btn btn--primary" data-role="confirm"></button>
        </div>
      </div>
    `;
    const titleEl   = overlay.querySelector<HTMLElement>('#admin-prompt-title')!;
    const messageEl = overlay.querySelector<HTMLElement>('#admin-prompt-message')!;
    const labelEl   = overlay.querySelector<HTMLElement>('#admin-prompt-label')!;
    const inputEl   = overlay.querySelector<HTMLInputElement>('#admin-prompt-input')!;
    const errorEl   = overlay.querySelector<HTMLElement>('#admin-prompt-error')!;
    const cancelBtn = overlay.querySelector<HTMLButtonElement>('[data-role="cancel"]')!;
    const confirmBtn= overlay.querySelector<HTMLButtonElement>('[data-role="confirm"]')!;
    const closeBtn  = overlay.querySelector<HTMLButtonElement>('.modal__close')!;

    titleEl.textContent = opts.title;
    if (opts.message) {
      messageEl.textContent = opts.message;
      messageEl.style.display = '';
    }
    labelEl.textContent = opts.label;
    inputEl.value = opts.value ?? '';
    if (opts.placeholder) inputEl.placeholder = opts.placeholder;
    if (opts.maxLength) inputEl.maxLength = opts.maxLength;
    if (opts.inputMode) inputEl.inputMode = opts.inputMode;
    cancelBtn.textContent = cancelLabel;
    confirmBtn.textContent = confirmLabel;

    const close = (result: string | null): void => {
      overlay!.classList.remove('is-open');
      document.removeEventListener('keydown', onKey);
      resolve(result);
    };
    const submit = (): void => {
      const value = inputEl.value.trim();
      const error = opts.validate?.(value) ?? null;
      if (error !== null) {
        errorEl.textContent = error;
        errorEl.style.display = 'block';
        inputEl.focus();
        return;
      }
      close(value);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close(null);
      else if (e.key === 'Enter') submit();
    };
    inputEl.addEventListener('input', () => { errorEl.style.display = 'none'; });
    cancelBtn.addEventListener('click', () => close(null));
    closeBtn.addEventListener('click', () => close(null));
    confirmBtn.addEventListener('click', submit);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(null); });
    document.addEventListener('keydown', onKey);

    overlay.classList.add('is-open');
    inputEl.focus();
    inputEl.select();
  });
}
